import { useCallback, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useSelector } from "react-redux";
import SendIcon from "@mui/icons-material/Send";
import {
  Box,
  FormControl,
  IconButton,
  InputAdornment,
  List,
  MenuItem,
  OutlinedInput,
  Select,
  Typography,
} from "@mui/material";
import { useCommentsContext, usePaginationContext } from "../../../hooks/FullscreenAnnotationModal";
import Comment from "../subcomponents/Comment";

const CommentsPanel = () => {
  const intl = useIntl();
  const { currentViewer } = useSelector(state => state.api);

  const {
    add: addComment,
    comments,
    removeComment,
    pageComments,
    pageCommentCount, selectedFigureId,
    availableVersions,
    currentVersionNumber,
    changeVersion,
  } = useCommentsContext();

  const {
    changePage,
    pageNo: currentPage,
    pageCount,
  } = usePaginationContext();

  const [newComment, setNewComment] = useState("");

  const handleAddComment = (event) => {
    if (!currentPage) {
      return;
    }

    if (event.key === 'Enter' || event.type === 'click') {
      if (newComment.trim()) {

        let figureIds = [];
        if (selectedFigureId && !pageComments(currentPage).find(n => n.connectedFigureIds.includes(selectedFigureId))) {
          figureIds.push(selectedFigureId);
        }

        addComment({ comment: newComment, pageNo: currentPage, figureIds });
        setNewComment("");
      }
    }
  };

  const handleDeleteComment = (commentId) => {
    removeComment(commentId);
  }

  const generateVersionMenuItems = useCallback(() => {
    const versionLabel = intl.formatMessage({ id: "common.version" });
    return availableVersions
      .sort((a, b) => a.versionNumber - b.versionNumber)
      .map((version, _) => {
        const { versionNumber } = version;
        return (
          <MenuItem
            key={versionNumber}
            value={versionNumber}
          >
            {versionLabel} {versionNumber}
          </MenuItem>
        );
      });
  }, [availableVersions, currentVersionNumber]);

  const generatePageMenuItems = useCallback(() => {
    const pageLabel = intl.formatMessage({ id: "common.page" });
    return [...Array(pageCount).keys().map(n => n + 1)]
      .filter(pageNo => (pageNo === currentPage || pageCommentCount(pageNo) > 0))
      .map((pageNo, _) => (
        <MenuItem
          key={pageNo}
          value={pageNo}
        >
          {pageLabel} {pageNo}
        </MenuItem>
      ));
  }, [comments, currentPage, pageCount]);

  useEffect(() => {
    setNewComment("");
  }, [comments]);

  return (
    <>
      <Typography variant="div"></Typography>
      {(availableVersions.length > 1) && (
        <Select
          disableUnderline
          onChange={(e) => changeVersion(e.target.value)}
          size="small"
          sx={{ position: "absolute", top: "4px", right: "25px", color: "rgba(0,0,0,0.67)", "& svg": { color: "rgba(0,0,0,0.34)" } }}
          value={currentVersionNumber}
          variant="standard"
        >
          {generateVersionMenuItems()}
        </Select>
      )}
      <Box display="flex" justifyContent="flex-start" alignItems="flex-end">
        <Typography variant="h6">
          <FormattedMessage id="context.comments" />
        </Typography>
        {(pageCount > -1) && (
          <Box>
            <Typography variant="span" p="0 0.2rem 0 0.3rem">
              <FormattedMessage id="common.for" />
            </Typography>
            <Select
              disableUnderline
              onChange={(e) => changePage(e.target.value)}
              size="small"
              sx={{ boxShadow: 'none', '.MuiInput-notchedOutline': { border: 0 } }}
              value={(currentPage || "").toString()}
              variant="standard"
            >
              {generatePageMenuItems()}
            </Select>
          </Box>
        )}
      </Box>
      <List sx={{ paddingBottom: "0px" }}>
        {pageComments(currentPage).map((comment, index) => (
          <Comment
            comment={comment}
            currentPage={currentPage}
            index={index}
            key={"comment_" + currentPage + "_" + index}
            onDeleteComment={handleDeleteComment}
            viewerUserId={currentViewer.viewer.userId}
          />
        ))}
      </List>
      <Box sx={{ width: '100%' }}>
        <FormControl fullWidth>
          <OutlinedInput
            disabled={currentPage < 1}
            fullWidth
            id="add-comment"
            onChange={(e) => setNewComment(e.target.value)}
            onKeyPress={handleAddComment}
            placeholder={`${intl.formatMessage({ id: "siteUsers.addAComment" })}...`}
            sx={{ background: "#ffffff" }}
            value={newComment}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="send-comment"
                  disabled={currentPage < 1}
                  edge="end"
                  onClick={handleAddComment}
                >
                  <SendIcon />
                </IconButton>
              </InputAdornment>
            }
          />
        </FormControl>
      </Box>
    </>
  );
};

export default CommentsPanel;
